import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';

import { get } from 'lodash';
import Button from 'react-bootstrap/Button';
import { reduxForm, formValueSelector } from 'redux-form';

import Loading from '../Loading';
import QuestionnaireHeader from './QuestionnaireHeader';
import QuestionnaireItem from './QuestionnaireItem';
import QuestionnaireStep from './QuestionnaireStep';
import QuestionnaireFieldCheckbox from './QuestionnaireFieldCheckbox';
import QuestionnaireFieldRadio from './QuestionnaireFieldRadio';
import QuestionnaireFieldTextarea from './QuestionnaireFieldTextarea';
import QuestionnaireFieldDate from './QuestionnaireFieldDate';

import bindName from './bindName';
import validate from './validate';

import './styles.module.css';

export class Questionnaire extends Component {
  constructor(props) {
    super(props);
    this.state = { activeStepIndex: 0 };
    
    this.getSteps = this.getSteps.bind(this);
    this.getActiveStepTitle = this.getActiveStepTitle.bind(this);
    this.progress = this.progress.bind(this);
    this.regress = this.regress.bind(this);
    
    this.renderCurrentQuestions = this.renderCurrentQuestions.bind(this);

    // Cache the steps - will need to change if updated dynamically
    this._steps = null;
  }

  /**
   * On componentDidUpdate
   *  - check if there is a new failed submission
   *  - check if there is a new successful submission
   * 
   * @params {Object} prevProps
   * @return {undefined}
   */
  componentDidUpdate(prevProps) {
    if (this.props.lastFailedField !== prevProps.lastFailedField) {
      this.focus(this.props.lastFailedField);
    }

    if (this.props.submitSucceeded && !prevProps.submitSucceeded) {
      window.scrollTo(0, 0); // go to list
    }
  }

  /**
   * Jump to a given field
   * 
   * @params {String} field name
   * @return {undefined}
   */ 
  focus(fieldName) {
    const steps = this.getSteps();
    for (let i = 0, found = false; i < steps.length && found === false; i++) {
      for (let j = 0; j < get(steps[i].props, 'children.length', 0) && found === false; j++) {
        if (steps[i].props.children[j].props.name === fieldName) {
          found = true;
          this.setState({ ...this.state, activeStepIndex: i });
        }
      }
    }
  }

/**
 * Utility that returns only QuestionnaireStep children, with cache
 * @return {Array} children that are QuestionnaireSteps
 */
  getSteps() {
    const steps = this.steps || this.props.children.filter(child => child && child.type === QuestionnaireStep);
    
    if (!this._steps) {
      this._steps = steps;
    }

    return steps;
  }

  /**
   * Returns the title of the currently active step
   * @return {String} title
   */
  getActiveStepTitle() {
    return this.getSteps()[this.state.activeStepIndex].props.title;
  }

  /**
   * Returns true if the first step is currently active
   * @return {Boolean}
   */
  isFirstStep() {
    return this.state.activeStepIndex === 0;
  }

  /**
   * Returns false if the first step is currently active
   * @return {Boolean}
   */
  isLastStep() {
    return this.state.activeStepIndex === this.getSteps().length - 1;
  }

  /**
   * Render the currently active questions
   * @return {Array} QuestionnaireItems
   */
  renderCurrentQuestions() {
    const { conditionMap } = this.props;
    const values = this.props.values || this.props.initialValues;
    const steps = this.getSteps();

    if (!Array.isArray(this.props.children)) {
      return this.props.children;
    }

    function reduceChildren(reducedChildren, child) {
      // First remove any falsey children
      if (!child) {
        return reducedChildren;
      }

      // Remove any QuestionnaireSteps that aren't currently active
      if (child.type === QuestionnaireStep && child.props.title !== this.getActiveStepTitle()) {
        return reducedChildren;
      }

      // Recursively reduce children
      let resolvedChild = child; // Use this if we don't need reduced children 
      if (child.props && Array.isArray(child.props.children)) {
        resolvedChild = React.cloneElement(child, { children: child.props.children.reduce(reduceChildren.bind(this), []) });
      }

      // Return early if we don't need to filter by conditionMap
      if (!conditionMap || !child.key || !conditionMap[child.key]) {
        reducedChildren.push(resolvedChild);
        return reducedChildren;
      }

      const conditions = conditionMap[child.key];
      const conditionFields = Object.keys(conditions);

      if (conditionFields.every(conditionField => values[conditionField] === conditions[conditionField])) {
        reducedChildren.push(resolvedChild);
        return reducedChildren;
      }

      return reducedChildren;
    }

    return this.props.children.reduce(reduceChildren.bind(this), []);
  }

  /**
   * Progress event callback
   * @param {Object} event object
   * @return {undefined} 
   */
  progress(e) {
    const nextStepIndex = this.state.activeStepIndex + 1;
    this.setState({
      ...this.state,
      activeStepIndex: nextStepIndex
    });
    window.scrollTo(0, 0);
    this.props.onProgress && this.props.onProgress(e);
  }

  /**
   * Regress event callback
   * @param {Object} event object
   * @return {undefined} 
   */
  regress(e) {
    const prevStepIndex = this.state.activeStepIndex - 1;
    this.setState({
      ...this.state,
      activeStepIndex: prevStepIndex
    });
    window.scrollTo(0, 0);
    this.props.onRegress && this.props.onRegress(e);
  }

  /**
   * Contextually returns form action CTAs
   * @return {Object}
   */
  renderCtas() {
    const isLastStep = this.isLastStep();
    const isFirstStep = this.isFirstStep();
    const progressStyle = isLastStep ? null : 'primary';
    const displayApproval = isLastStep && this.props.displayApprovalAction();
    const displayReject = isLastStep && this.props.displayRejectAction();
    const completeStyle = displayApproval ? null : 'primary';

    return (
      <div styleName="actions">
        {!isFirstStep && <Button onClick={this.regress}>{this.props.regressCta}</Button>}
        {displayApproval && <Button styleName="right" variant="primary" onClick={this.props.onApprove}>{this.props.approveCta}</Button>}
        {displayReject && <Button styleName="right" variant="danger" onClick={this.props.onReject}>{this.props.rejectCta}</Button>}
        {isLastStep && <Button styleName="right" variant={completeStyle} type="submit">{this.props.completeCta}</Button>}
        {isLastStep && <Button styleName="right" variant={progressStyle} onClick={this.props.onProgress}>{this.props.progressFinalCta}</Button>}
        {!isLastStep  && <Button styleName="right" variant="primary" onClick={this.progress}>{this.props.progressCta}</Button>}
      </div>
    );
  }

  /**
   * On render, set the submission action to either this.progress or the passed submission callback
   * @return {Object}
   */
  render() {
    const { children, saving } = this.props;
    const submitAction = this.isLastStep() ? this.props.onSubmit : this.progress;

    if (saving) {
      return <Loading show />;
    }

    return (
      <form styleName="wrap" onSubmit={this.props.handleSubmit(submitAction)}>
        {this.renderCurrentQuestions()}
        {this.renderCtas()}
      </form>
    );
  }
}

Questionnaire.defaultProps = {
  progressCta: 'Next',
  progressFinalCta: 'Save',
  regressCta: 'Back',
  completeCta: 'Mark as Complete',
  approveCta: 'Approve',
  rejectCta: 'Reject'
};

Questionnaire.propTypes = {
  saving: PropTypes.bool,
  children: PropTypes.array.isRequired,
  form: PropTypes.string.isRequired,
  onProgress: PropTypes.func,
  onRegress: PropTypes.func,
  onApprove: PropTypes.func,
  onReject: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  progressCta: PropTypes.string,
  progressFinalCta: PropTypes.string,
  approveCta: PropTypes.string,
  rejectCta: PropTypes.string,
  regressCta: PropTypes.string,
  completeCta: PropTypes.string,
  initialValues: PropTypes.object,
  displayApprovalAction: PropTypes.func,
  displayRejectAction: PropTypes.func.isRequired,
  conditionMap: PropTypes.object,
  lastFailedField: PropTypes.string,
  
  // provided by redux-forms
  values: PropTypes.object,
  handleSubmit: PropTypes.func.isRequired,
  submitFailed: PropTypes.bool.isRequired,
  submitSucceeded: PropTypes.bool.isRequired
};

Questionnaire.Header = QuestionnaireHeader;
Questionnaire.Step   = QuestionnaireStep;
Questionnaire.Item   = QuestionnaireItem;

Questionnaire.Checkbox = QuestionnaireFieldCheckbox;
Questionnaire.Radios   = QuestionnaireFieldRadio;
Questionnaire.Textarea = QuestionnaireFieldTextarea;
Questionnaire.Date     = QuestionnaireFieldDate;

Questionnaire.bindName = bindName;
Questionnaire.validate = validate;

// Don't define the form id here as we use a prop instead
// enableReinitialize ensures the form resets if initialValues change
export default reduxForm({ enableReinitialize: true })(Questionnaire);
